import React, { useEffect, useMemo, useRef, useState } from "react";
import clsx from "clsx";
import { AlertTriangle, FilePlus2, FileText, LocateFixed, Maximize2, Pencil, Plus, Trash2 } from "lucide-react";
import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import { buildManualAddendumLabel, findReferenceSuggestion } from "../utils/pdfAddendumLabels";
import { inspectWinAnsiText } from "../utils/pdfTextEncoding";

let tempKeySeed = 0;
const newTempKey = () => `new-${Date.now()}-${tempKeySeed++}`;

export const getAddendumKey = (addendum) => (addendum.id != null ? `id-${addendum.id}` : addendum.tempKey);

/**
 * Position of an addendum in reading order: the page its reference points to;
 * a reference the PDF does not know after every page; a general addendum (no
 * reference) last.
 */
export const getAddendumRank = (addendum) => {
    if (!addendum.reference) return Number.MAX_SAFE_INTEGER;
    return addendum.pageNumber || Number.MAX_SAFE_INTEGER - 1;
};

/** Reading order. Ties keep the order the user gave them. */
export const sortAddendums = (addendums) =>
    addendums
        .map((addendum, index) => ({ addendum, index }))
        .sort((a, b) => getAddendumRank(a.addendum) - getAddendumRank(b.addendum) || a.index - b.index)
        .map(({ addendum }) => addendum);

const emptyDraft = { reference: "", text: "" };

/** Reference and page a draft resolves to: the page is only known when the reference matches the PDF. */
const resolveDraft = (draft, suggestions) => {
    const reference = draft.reference.trim();
    if (!reference) return { reference: null, pageNumber: null };
    return { reference, pageNumber: findReferenceSuggestion(reference, suggestions)?.pageNumber || null };
};

const groupTitle = (addendum) => {
    if (!addendum.reference) return "General";
    return addendum.pageNumber ? `Page ${addendum.pageNumber}` : "Other references";
};

const EncodingWarning = ({ text }) => {
    const { replaced, removed } = useMemo(() => inspectWinAnsiText(text), [text]);
    if (!replaced.length && !removed.length) return null;

    return (
        <div className="adp-warning" role="status">
            <AlertTriangle size={16} />
            <div>
                {replaced.length > 0 && (
                    <p>
                        Will be replaced in the PDF:{" "}
                        {replaced.map(({ char, with: replacement }) => (
                            <span key={char} className="adp-warning__pair">
                                <code>{char}</code> as <code>{replacement}</code>
                            </span>
                        ))}
                    </p>
                )}
                {removed.length > 0 && (
                    <p>
                        Not supported by the PDF, will be removed:{" "}
                        {removed.map((char) => (
                            <code key={char}>{char}</code>
                        ))}
                    </p>
                )}
            </div>
        </div>
    );
};

const AddendumEditor = ({ initialDraft, suggestions, formCode, isNew, onSave, onCancel }) => {
    const [draft, setDraft] = useState(initialDraft);
    const referenceRef = useRef(null);

    useEffect(() => {
        referenceRef.current?.focus({ preventScroll: true });
    }, []);

    const update = (changes) => setDraft((prev) => ({ ...prev, ...changes }));
    const resolved = resolveDraft(draft, suggestions);
    const canSave = draft.text.trim().length > 0;

    const onSubmit = () => {
        if (canSave) onSave({ ...resolved, text: draft.text });
    };

    const submitOnModEnter = (e) => {
        if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
            e.preventDefault();
            onSubmit();
        }
    };

    return (
        <div className="adp-editor">
            <h4 className="adp-editor__title">{isNew ? "New addendum" : "Edit addendum"}</h4>

            <label className="adp-field">
                <span className="adp-field__label">Section / item</span>
                <input
                    ref={referenceRef}
                    type="text"
                    list="adp-reference-suggestions"
                    value={draft.reference}
                    maxLength={500}
                    placeholder="e.g. Part 2, Item 3.A."
                    onChange={(e) => update({ reference: e.target.value })}
                    onKeyDown={(e) => {
                        // An <input> inside the page would otherwise submit the PDF form
                        if (e.key === "Enter" && !e.metaKey && !e.ctrlKey) e.preventDefault();
                        submitOnModEnter(e);
                    }}
                />
                <datalist id="adp-reference-suggestions">
                    {suggestions.map(({ value, hint }) => (
                        <option key={value} value={value} label={hint} />
                    ))}
                </datalist>
                <span className="adp-field__hint">
                    {!resolved.reference
                        ? "Leave empty for an addendum about the whole form."
                        : resolved.pageNumber
                          ? `Found in the form, page ${resolved.pageNumber}.`
                          : "Printed as written."}
                </span>
            </label>

            <label className="adp-field adp-field--grow">
                <span className="adp-field__label">Text</span>
                <textarea
                    value={draft.text}
                    onChange={(e) => update({ text: e.target.value })}
                    placeholder="Write the addendum…"
                    onKeyDown={submitOnModEnter}
                />
            </label>

            <EncodingWarning text={`${draft.reference}\n${draft.text}`} />

            <div className="adp-preview">
                <span className="adp-preview__caption">Title in the PDF</span>
                <span className="adp-preview__label">{buildManualAddendumLabel({ formCode, ...resolved })}</span>
            </div>

            <div className="adp-editor__actions">
                <button type="button" className="adp-btn" onClick={onCancel}>
                    Cancel
                </button>
                <button type="button" className="adp-btn primary" onClick={onSubmit} disabled={!canSave}>
                    {isNew ? "Add addendum" : "Save changes"}
                </button>
            </div>
        </div>
    );
};

/** Full text of an addendum too long for its card. */
const AddendumTextDialog = ({ addendum, open, readOnly, onClose, onEdit }) => (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth scroll="paper" aria-labelledby="adp-dialog-title">
        <DialogTitle id="adp-dialog-title" className="adp-dialog__title">
            {addendum.reference || "Whole form"}
            {addendum.reference && addendum.pageNumber ? <span className="adp-dialog__page">Page {addendum.pageNumber}</span> : null}
        </DialogTitle>
        <DialogContent dividers>
            <p className="adp-dialog__text">{addendum.text}</p>
        </DialogContent>
        <DialogActions>
            <Button onClick={onClose}>Close</Button>
            {!readOnly && (
                <Button
                    variant="contained"
                    color="primary"
                    onClick={() => {
                        onClose();
                        onEdit();
                    }}
                >
                    Edit
                </Button>
            )}
        </DialogActions>
    </Dialog>
);

const AddendumCard = ({ addendum, readOnly, canLocate, onEdit, onDelete, onLocate }) => {
    const [confirmingDelete, setConfirmingDelete] = useState(false);
    const [isTruncated, setIsTruncated] = useState(false);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const textRef = useRef(null);

    // The text is capped by CSS; only when it actually overflows does the card offer the full view.
    // Re-measured on resize, since the panel's width decides how many lines the text takes.
    useEffect(() => {
        const element = textRef.current;
        if (!element) return undefined;
        const measure = () => setIsTruncated(element.scrollHeight > element.clientHeight + 1);
        measure();
        const observer = new ResizeObserver(measure);
        observer.observe(element);
        return () => observer.disconnect();
    }, [addendum.text]);

    return (
        <article className={clsx("adp-card", { confirming: confirmingDelete })}>
            <div className="adp-card__head">
                <span className="adp-card__item">{addendum.reference || "Whole form"}</span>
                <div className="adp-card__actions">
                    {canLocate && (
                        <button type="button" className="adp-icon-btn" onClick={onLocate} title="Locate in the PDF">
                            <LocateFixed size={14} />
                        </button>
                    )}
                    {!readOnly && (
                        <>
                            <button type="button" className="adp-icon-btn" onClick={onEdit} title="Edit">
                                <Pencil size={14} />
                            </button>
                            <button type="button" className="adp-icon-btn danger" onClick={() => setConfirmingDelete(true)} title="Delete">
                                <Trash2 size={14} />
                            </button>
                        </>
                    )}
                </div>
            </div>
            <p ref={textRef} className={clsx("adp-card__text", { truncated: isTruncated })}>
                {addendum.text}
            </p>
            {isTruncated && (
                <button type="button" className="adp-card__more" onClick={() => setIsDialogOpen(true)}>
                    <Maximize2 size={13} />
                    Show full text
                </button>
            )}
            {confirmingDelete && (
                <div className="adp-card__confirm">
                    <span>Delete this addendum?</span>
                    <button type="button" className="adp-btn small" onClick={() => setConfirmingDelete(false)}>
                        Keep
                    </button>
                    <button type="button" className="adp-btn small danger" onClick={onDelete}>
                        Delete
                    </button>
                </div>
            )}
            <AddendumTextDialog
                addendum={addendum}
                open={isDialogOpen}
                readOnly={readOnly}
                onClose={() => setIsDialogOpen(false)}
                onEdit={onEdit}
            />
        </article>
    );
};

/**
 * Side panel listing the addendums written by hand for a form. Always visible
 * next to the PDF; editable unless readOnly. Changes live in the parent's state
 * and are persisted with the form's Save.
 */
const AddendumPanel = ({ addendums = [], suggestions = [], formCode, readOnly, onChange = () => {}, onLocate, onEditingChange = () => {} }) => {
    // null: browsing; { key: null } adding; { key } editing that addendum
    const [editing, setEditing] = useState(null);

    useEffect(() => {
        onEditingChange(Boolean(editing));
    }, [editing]); // eslint-disable-line react-hooks/exhaustive-deps

    const sorted = useMemo(() => sortAddendums(addendums), [addendums]);

    const groups = useMemo(
        () =>
            sorted.reduce((acc, addendum) => {
                const title = groupTitle(addendum);
                const last = acc[acc.length - 1];
                if (last && last.title === title) last.addendums.push(addendum);
                else acc.push({ title, addendums: [addendum] });
                return acc;
            }, []),
        [sorted],
    );

    const editingAddendum = editing?.key ? addendums.find((x) => getAddendumKey(x) === editing.key) : null;

    const onSave = (values) => {
        if (editingAddendum) {
            onChange(sortAddendums(addendums.map((x) => (x === editingAddendum ? { ...x, ...values } : x))));
        } else {
            onChange(sortAddendums([...addendums, { ...values, tempKey: newTempKey() }]));
        }
        setEditing(null);
    };

    const onDelete = (addendum) => onChange(addendums.filter((x) => x !== addendum));

    return (
        <aside className="addendum-panel" aria-label="Addendums">
            <header className="adp-header">
                <FileText size={18} />
                <h3 className="adp-header__title">Addendums</h3>
                <span className="adp-header__count">{addendums.length}</span>
                {!readOnly && !editing && (
                    <button type="button" className="adp-btn primary small" onClick={() => setEditing({ key: null })}>
                        <Plus size={14} />
                        Add
                    </button>
                )}
            </header>

            {editing ? (
                <AddendumEditor
                    key={editing.key || "new"}
                    initialDraft={editingAddendum ? { reference: editingAddendum.reference || "", text: editingAddendum.text } : emptyDraft}
                    suggestions={suggestions}
                    formCode={formCode}
                    isNew={!editingAddendum}
                    onSave={onSave}
                    onCancel={() => setEditing(null)}
                />
            ) : groups.length === 0 ? (
                <div className="adp-empty">
                    <FilePlus2 size={28} />
                    <p>
                        {readOnly
                            ? "This form has no manual addendums."
                            : "No addendums yet. Add one to include extra information for a section of the form."}
                    </p>
                    <p className="adp-empty__hint">Text that does not fit in a field is still added to the addendum automatically.</p>
                </div>
            ) : (
                <div className="adp-list">
                    {groups.map(({ title, addendums: groupAddendums }) => (
                        <section key={title} className="adp-group" aria-label={title}>
                            <h4 className="adp-group__header">{title}</h4>
                            {groupAddendums.map((addendum) => (
                                <AddendumCard
                                    key={getAddendumKey(addendum)}
                                    addendum={addendum}
                                    readOnly={readOnly}
                                    canLocate={Boolean(addendum.reference && addendum.pageNumber)}
                                    onLocate={() => onLocate(addendum)}
                                    onEdit={() => setEditing({ key: getAddendumKey(addendum) })}
                                    onDelete={() => onDelete(addendum)}
                                />
                            ))}
                        </section>
                    ))}
                </div>
            )}
        </aside>
    );
};

export default AddendumPanel;
